import { SBCAAS_CLASSIC_LABEL, SBCAAS_MODERN_LABEL } from '@/constants/sbcaas';

// ─── Error / Status Message Constants ───────────────────────────────────────

export const INVALID_USERNAME_PASSWORD =
    'Login unsuccessful due to invalid username or password';
export const EXPIRED_PASSWORD = 'Login unsuccessful due to expired password';
export const DISABLED_DELETE_USER =
    'Login unsuccessful due to disabled or deleted user';
export const CONDITIONAL_ACCESS_REQUIREMENT =
    'Login unsuccessful due to conditional access requirement';
export const MFA_REQUIREMENT = 'Login unsuccessful due to MFA requirement';
export const ACCESS_MICROSOFT_FAILED =
    'Access via Microsoft Graph failed (no roles returned)';

export const NO_VOICE_POLICIES =
    'Provisioning DNS (Incomplete: Missing Voice Policies)';
export const NO_DIAL_PLANS =
    'Provisioning DNS (Incomplete: Missing Dial Plans)';
export const NO_REGIONS = 'Provisioning DNS (Incomplete: Missing Regions)';
export const APP_AUTH_ACCESS_REQUIRED =
    'Graph API access requested. No credentials to sync with';
export const CALLING_DISABLED =
    'Service cannot connect due to expired Graph API failures. Please Grant Access to restore';
export const ZOOM_NO_CONFIG =
    'Zoom Provider Exchange is not configured as access has not been provided. Please Grant Access';
export const EIGHTBYEIGHT_NO_CONFIG =
    '8x8 Provider Exchange is not configured as access has not been provided. Please Grant Access';
export const WEBEX_NO_CONFIG =
    'Webex Provider Exchange is not configured as access has not been provided. Please Grant Access';
export const RINGCENTRAL_NO_CONFIG =
    'Ring Central Provider Exchange is not configured as access has not been provided. Please Grant Access';
export const DIALPAD_NO_CONFIG =
    'Dial Pad Provider Exchange is not configured as access has not been provided. Please Grant Access';
export const SERVICE_CANNOT_CONNECT_DISABLED =
    'Service cannot connect. Please Grant Access. Calling may be disabled if this issue is not resolved';
export const SERVICE_NO_CONNECT =
    ' Service cannot connect. Please Grant Access.';

const ERROR_TOOLTIP = 'Please contact support for assistance.';

// ─── Service Type Constants ───────────────────────────────────────────────────

export const APP_AUTH = 'AppAuth';
// Dedicated status key for the GCC High Microsoft app-auth. Both Microsoft
// app-auths share appAuthTypeID 'Microsoft', so the GCCH card needs its own
// status slot (keyed by platformID) to avoid colliding with the commercial
// Microsoft app-auth stored under APP_AUTH.
export const APP_AUTH_GCCH = 'AppAuthGCCH';
export const MICROSOFT_GRAPH = 'Microsoft';
export const OPERATOR_CONNECT = 'OperatorConnect';
export const OPERATOR_CONNECT_CONFERENCING = 'OperatorConnectConferencing';
export const DIRECT_ROUTING = 'DirectRouting';
export const SBC = 'SBCaaS';
export const ZOOM = 'ZoomPEX';
export const ZOOM_AUTH = 'Zoom';
export const EIGHTBYEIGHT_AUTH = 'EightByEightAppAuth';
export const EIGHTBYEIGHT = 'EightByEight';
export const RINGCENTRAL_AUTH = 'RingCentralAppAuth';
export const RINGCENTRAL = 'RingCentral';
export const DIALPAD_AUTH = 'DialPadAppAuth';
export const DIALPAD = 'DialPad';
export const ACS = 'ACSDirectRouting';
export const TPM = 'TeamsPhoneMobile';
export const VENDOR_DEFINED = 'VendorDefined';
export const GCC_HIGH = 'DirectRoutingGCCH';
export const WEBEX = 'Webex';
export const WEBEX_AUTH = 'WebexAppAuth';
export const OPERATOR_PLATFORM = 'OperatorPlatform';
export const SRT = 'SRT';

// ─── Service Type Label Lookup ────────────────────────────────────────────────

const SERVICE_TYPE_LABELS = {
    ACSDirectRouting: 'ACS Direct Routing',
    TeamsPhoneMobile: 'Teams Phone Mobile',
    OperatorConnect: 'Operator Connect',
    DirectRouting: 'Direct Routing',
    SBCaaS: SBCAAS_CLASSIC_LABEL,
    OperatorConnectConferencing: 'Operator Connect Conferencing',
    VendorDefined: 'Vendor Defined',
    ZoomPEX: 'Zoom',
    EightByEight: '8x8',
    GCCHighDirectRouting: 'GCC High',
    Webex: 'Webex',
    RingCentral: 'Ring Central',
    DialPad: 'Dial Pad',
    OperatorPlatform: 'Operator Platform',
    SRT: SBCAAS_MODERN_LABEL,
};

export const serviceTypeRender = (rowData) =>
    SERVICE_TYPE_LABELS[rowData.serviceType] ?? null;

// ─── Status Base Configuration ────────────────────────────────────────────────

export const statusBaseConfig = {
    menu: {
        EditOnly: {
            showEdit: true,
            grantAccess: false,
            showTenantId: false,
        },
        EditAndAccess: {
            showEdit: true,
            grantAccess: true,
            showTenantId: false,
        },
    },
    actions: {
        GrantAccess: {
            actionIcon: 'Settings',
            actionIconTooltip: 'Grant Access',
        },
        ResetServiceAdmin: {
            actionIcon: 'Settings',
            actionIconTooltip: 'Reset Service Admin',
        },
        SyncSetupGuide: {
            actionIcon: 'ExternalLink',
            actionIconTooltip: 'Sync Setup Guide',
        },
        EditService: {
            actionIcon: 'Edit',
            actionIconTooltip: 'Edit Service',
        },
        ActivationGuide: {
            actionIcon: 'ExternalLink',
            actionIconTooltip: 'Activation Guide',
        },
        ConfirmActivation: {
            actionIcon: 'CheckCircle',
            actionIconTooltip: 'Confirm Activation',
        },
        None: {
            actionIcon: null,
            actionIconTooltip: null,
        },
    },
};

// ─── Shared Status Shapes ─────────────────────────────────────────────────────
// Private helpers — not exported. Eliminates repeated statusBaseConfig spreads.

const S_HEALTHY = {
    color: 'green',
    animation: false,
    tooltip: null,
    tooltipIcon: null,
    ...statusBaseConfig.actions.None,
    ...statusBaseConfig.menu.EditOnly,
};

// "Confirming Access" / "Pair Pending" spinner — used by auth service statuses
const S_CONFIRMING_ACCESS = {
    color: 'green',
    animation: true,
    tooltip: 'Confirming Access. Please wait',
    tooltipIcon: 'Spinner',
    ...statusBaseConfig.actions.None,
    ...statusBaseConfig.menu.EditOnly,
};

const S_REINITIALIZING = {
    color: 'green',
    animation: true,
    tooltip: 'Service is currently re-establishing access. Please wait',
    tooltipIcon: 'Spinner',
    ...statusBaseConfig.actions.None,
    ...statusBaseConfig.menu.EditOnly,
};

const S_COMMON_ERROR = {
    color: 'red',
    animation: false,
    tooltip: ERROR_TOOLTIP,
    tooltipIcon: 'Warning',
    ...statusBaseConfig.actions.GrantAccess,
    ...statusBaseConfig.menu.EditAndAccess,
};

// Status 21 in MS/GCC auth — service cannot connect
const S_DEGRADED_CONNECT = {
    color: 'yellow',
    animation: false,
    tooltip: SERVICE_CANNOT_CONNECT_DISABLED,
    tooltipIcon: 'Info',
    ...statusBaseConfig.actions.GrantAccess,
    ...statusBaseConfig.menu.EditAndAccess,
};

// Status 21 in Zoom/Webex auth
const S_DEGRADED_NO_CONNECT = {
    color: 'yellow',
    animation: false,
    tooltip: SERVICE_NO_CONNECT,
    tooltipIcon: 'Info',
    ...statusBaseConfig.actions.GrantAccess,
    ...statusBaseConfig.menu.EditAndAccess,
};

// Status 21 in 8x8 / RingCentral auth
const S_INCOMPLETE_CONFIG = {
    color: 'yellow',
    animation: false,
    tooltip: 'API Key has not been configured. Please complete setup.',
    tooltipIcon: 'Info',
    ...statusBaseConfig.actions.GrantAccess,
    ...statusBaseConfig.menu.EditAndAccess,
};

// ─── Status Display Extension Mapping ────────────────────────────────────────

export const statusDisplayExtendMapping = {
    [ACCESS_MICROSOFT_FAILED]: {
        ...statusBaseConfig.actions.GrantAccess,
        ...statusBaseConfig.menu.EditAndAccess,
    },
    [DISABLED_DELETE_USER]: {
        ...statusBaseConfig.actions.ResetServiceAdmin,
        ...statusBaseConfig.menu.EditOnly,
    },
    [INVALID_USERNAME_PASSWORD]: {
        ...statusBaseConfig.actions.ResetServiceAdmin,
        ...statusBaseConfig.menu.EditOnly,
    },
    [MFA_REQUIREMENT]: {
        ...statusBaseConfig.actions.SyncSetupGuide,
        ...statusBaseConfig.menu.EditOnly,
        link: '/Guides/MFA_for_TCAP.pdf',
    },
    [CONDITIONAL_ACCESS_REQUIREMENT]: {
        ...statusBaseConfig.actions.SyncSetupGuide,
        ...statusBaseConfig.menu.EditOnly,
        link: '/Guides/MFA_for_TCAP.pdf',
    },
    [EXPIRED_PASSWORD]: {
        ...statusBaseConfig.actions.ResetServiceAdmin,
        ...statusBaseConfig.menu.EditOnly,
    },
};

export const menuAppAuthMapping = {
    NoAppAuth: {
        ...statusBaseConfig.menu.EditAndAccess,
    },
    HasAppAuth: {
        ...statusBaseConfig.menu.EditOnly,
    },
};

// ─── Service Data Definitions ─────────────────────────────────────────────────

export const appAuthData = {
    statusId: {
        0: {
            Healthy: S_HEALTHY,
            statusLabels: ['Healthy'],
        },
        2: {
            'Access Required': {
                color: 'yellow',
                animation: false,
                tooltip: 'Please Grant Access to the service',
                tooltipIcon: 'Info',
                ...statusBaseConfig.actions.GrantAccess,
                ...statusBaseConfig.menu.EditAndAccess,
            },
            statusLabels: ['Access Required'],
        },
        4: {
            Initializing: S_CONFIRMING_ACCESS,
            statusLabels: ['Initializing'],
        },
        5: {
            Reinitializing: S_REINITIALIZING,
            statusLabels: ['Reinitializing'],
        },
        20: {
            Degraded: {
                color: 'yellow',
                animation: false,
                tooltip: null, // statusDisplayExtended gonna display tooltip
                tooltipIcon: 'Info',
                //statusDisplayExtendMapping -- special case
            },
            Error: S_COMMON_ERROR,
            statusLabels: ['Degraded', 'Error'],
        },
        21: {
            Degraded: S_DEGRADED_CONNECT,
            statusLabels: ['Degraded'],
        },
        '-1': {
            'Service Account Error': {
                color: 'yellow',
                animation: false,
                tooltip:
                    'Service Account issue detected. This may be impacting some service features. Please refer to the Sync Setup guide to restore access',
                tooltipIcon: 'Info',
                ...statusBaseConfig.actions.SyncSetupGuide,
                ...statusBaseConfig.menu.EditAndAccess,
                link: '/Guides/MFA_for_TCAP.pdf',
            },
            Reinitializing: S_REINITIALIZING,
            statusLabels: ['Service Account Error', 'Reinitializing'],
        },
    },
    statusIds: [0, 2, 4, 5, 20, 21],
};

// Identical structure to appAuthData
export const gccHighAppAuthData = appAuthData;

export const operatorConnectData = {
    statusId: {
        0: {
            Healthy: {
                color: 'green',
                animation: false,
                tooltip: null,
                tooltipIcon: null,
                ...statusBaseConfig.actions.None,
                // menuAppAuthMapping -- special case
            },
            statusLabels: ['Healthy'],
        },
        2: {
            'Access Required': {
                color: 'yellow',
                animation: false,
                tooltip:
                    'Operator Connect is not configured as access has not been provided. Please Grant Access or provide a Tenant ID',
                tooltipIcon: 'Info',
                ...statusBaseConfig.actions.GrantAccess,
                showEdit: true,
                grantAccess: true,
                showTenantId: true,
            },
            statusLabels: ['Access Required'],
        },
        20: {
            Error: S_COMMON_ERROR,
            statusLabels: ['Error'],
        },
        21: {
            Degraded: S_DEGRADED_CONNECT,
            statusLabels: ['Degraded'],
        },
    },
    statusIds: [0, 2, 20, 21],
};

export const directRoutingData = {
    statusId: {
        0: {
            Healthy: S_HEALTHY,
            statusLabels: ['Healthy'],
        },
        2: {
            'Confirming Access': {
                color: 'green',
                animation: true,
                tooltip: 'Checking initial Graph API access',
                tooltipIcon: 'Spinner',
                ...statusBaseConfig.actions.None,
                ...statusBaseConfig.menu.EditOnly,
            },
            'Access Required': {
                color: 'yellow',
                animation: false,
                tooltip:
                    'Direct Routing cannot be configured as Sync permissions have not been provided. Please Activate Sync',
                tooltipIcon: 'Info',
                ...statusBaseConfig.actions.GrantAccess,
                ...statusBaseConfig.menu.EditAndAccess,
            },
            'Approval Required': {
                color: 'yellow',
                animation: false,
                tooltip:
                    'Services will not provision until your company has been approved. Please contact your partner for more information',
                tooltipIcon: 'Info',
                ...statusBaseConfig.actions.None,
                ...statusBaseConfig.menu.EditOnly,
            },
            statusLabels: [
                'Confirming Access',
                'Access Required',
                'Approval Required',
            ],
        },
        4: {
            Initializing: {
                color: 'green',
                animation: true,
                tooltip: 'Pairing with Teams. Please wait',
                tooltipIcon: 'Spinner',
                ...statusBaseConfig.actions.None,
                ...statusBaseConfig.menu.EditOnly,
            },
            statusLabels: ['Initializing'],
        },
        10: {
            'Provisioning DNS': {
                color: 'green',
                animation: true,
                tooltip:
                    'Direct Routing domains are being created and validated. Please wait',
                tooltipIcon: 'Spinner',
                ...statusBaseConfig.actions.None,
                showEdit: false,
                grantAccess: false,
                showTenantId: false,
            },
            'Region not set': {
                color: 'yellow',
                animation: false,
                tooltip:
                    'Direct Routing requires at least one Region to be assigned to the service.  Please edit service to continue',
                tooltipIcon: 'Info',
                ...statusBaseConfig.actions.EditService,
                ...statusBaseConfig.menu.EditOnly,
            },
            statusLabels: ['Provisioning DNS', 'Region not set'],
        },
        11: {
            'No Voice Policies': {
                color: 'yellow',
                animation: false,
                tooltip:
                    'Direct Routing requires at least one Voice Policy to be assigned to the service.  Please edit service to continue',
                tooltipIcon: 'Info',
                ...statusBaseConfig.actions.EditService,
                ...statusBaseConfig.menu.EditOnly,
            },
            'No Dial Plans': {
                color: 'yellow',
                animation: false,
                tooltip:
                    'Direct Routing requires at least one Dial Plan to be assigned to the service.  Please edit service to continue',
                tooltipIcon: 'Info',
                ...statusBaseConfig.actions.EditService,
                ...statusBaseConfig.menu.EditOnly,
            },
            'Provisioning SBC': {
                color: 'green',
                animation: true,
                tooltip:
                    'SBC is being provisioned for Direct Routing.  Please wait',
                tooltipIcon: 'Spinner',
                ...statusBaseConfig.actions.None,
                showEdit: false,
                grantAccess: false,
                showTenantId: false,
            },
            statusLabels: [
                'No Voice Policies',
                'No Dial Plans',
                'Provisioning SBC',
            ],
        },
        12: {
            'No Voice Policies': {
                color: 'yellow',
                animation: false,
                tooltip:
                    'Direct Routing requires at least one Voice Policy to be assigned to the service.  Please edit service to continue',
                tooltipIcon: 'Info',
                ...statusBaseConfig.actions.EditService,
                ...statusBaseConfig.menu.EditOnly,
            },
            'No Dial Plans': {
                color: 'yellow',
                animation: false,
                tooltip:
                    'Direct Routing requires at least one Dial Plan to be assigned to the service.  Please edit service to continue',
                tooltipIcon: 'Info',
                ...statusBaseConfig.actions.EditService,
                ...statusBaseConfig.menu.EditOnly,
            },
            'Activating Teams Phone': {
                color: 'green',
                animation: true,
                tooltip:
                    'Enabling Enterprise Voice on new domains. Please wait',
                tooltipIcon: 'Spinner',
                ...statusBaseConfig.actions.None,
                showEdit: true,
                grantAccess: false,
                showTenantId: false,
            },
            statusLabels: [
                'No Voice Policies',
                'No Dial Plans',
                'Activating Teams Phone',
            ],
        },
        13: {
            'No Voice Policies': {
                color: 'yellow',
                animation: false,
                tooltip:
                    'Direct Routing requires at least one Voice Routing Policy to be assigned to the service.  Please edit service to continue',
                tooltipIcon: 'Info',
                ...statusBaseConfig.actions.EditService,
                ...statusBaseConfig.menu.EditOnly,
            },
            'No Dial Plans': {
                color: 'yellow',
                animation: false,
                tooltip:
                    'Direct Routing requires at least one Dial Plan to be assigned to the service.  Please edit service to continue',
                tooltipIcon: 'Info',
                ...statusBaseConfig.actions.EditService,
                ...statusBaseConfig.menu.EditOnly,
            },
            'Provisioning Teams Phone': {
                color: 'green',
                animation: true,
                tooltip: 'Creating Voice Routing Policies and Dial Plans',
                tooltipIcon: 'Spinner',
                ...statusBaseConfig.actions.None,
                showEdit: true,
                grantAccess: false,
                showTenantId: false,
            },
            statusLabels: [
                'No Voice Policies',
                'No Dial Plans',
                'Provisioning Teams Phone',
            ],
        },
        20: {
            Error: S_COMMON_ERROR,
            statusLabels: ['Error'],
        },
    },
    statusIds: [0, 2, 3, 4, 10, 11, 12, 13, 20],
};

export const gccHighData = {
    statusId: {
        0: {
            Healthy: S_HEALTHY,
            statusLabels: ['Healthy'],
        },
        2: {
            'Confirming Access': {
                color: 'green',
                animation: true,
                tooltip: 'Checking initial Graph API access',
                tooltipIcon: 'Spinner',
                ...statusBaseConfig.actions.None,
                ...statusBaseConfig.menu.EditOnly,
            },
            'Access Required': {
                color: 'yellow',
                animation: false,
                tooltip:
                    'Direct Routing GCCH cannot be configured as Sync permissions have not been provided. Please Activate Sync',
                tooltipIcon: 'Info',
                ...statusBaseConfig.actions.GrantAccess,
                ...statusBaseConfig.menu.EditAndAccess,
            },
            'Approval Required': {
                color: 'yellow',
                animation: false,
                tooltip:
                    'Services will not provision until your company has been approved. Please contact your partner for more information',
                tooltipIcon: 'Info',
                ...statusBaseConfig.actions.None,
                ...statusBaseConfig.menu.EditOnly,
            },
            statusLabels: [
                'Confirming Access',
                'Access Required',
                'Approval Required',
            ],
        },
        4: {
            Initializing: {
                color: 'green',
                animation: true,
                tooltip: 'Pairing with Teams. Please wait',
                tooltipIcon: 'Spinner',
                ...statusBaseConfig.actions.None,
                ...statusBaseConfig.menu.EditOnly,
            },
            statusLabels: ['Initializing'],
        },
        10: {
            'Provisioning DNS': {
                color: 'green',
                animation: true,
                tooltip:
                    'Direct Routing domains are being created and validated. Please wait',
                tooltipIcon: 'Spinner',
                ...statusBaseConfig.actions.None,
                showEdit: false,
                grantAccess: false,
                showTenantId: false,
            },
            'Region not set': {
                color: 'yellow',
                animation: false,
                tooltip:
                    'Direct Routing requires at least one Region to be assigned to the service.  Please edit service to continue',
                tooltipIcon: 'Info',
                ...statusBaseConfig.actions.EditService,
                ...statusBaseConfig.menu.EditOnly,
            },
            statusLabels: ['Provisioning DNS', 'Region not set'],
        },
        11: {
            'No Voice Policies': {
                color: 'yellow',
                animation: false,
                tooltip:
                    'Direct Routing requires at least one Voice Policy to be assigned to the service.  Please edit service to continue',
                tooltipIcon: 'Info',
                ...statusBaseConfig.actions.EditService,
                ...statusBaseConfig.menu.EditOnly,
            },
            'No Dial Plans': {
                color: 'yellow',
                animation: false,
                tooltip:
                    'Direct Routing requires at least one Dial Plan to be assigned to the service.  Please edit service to continue',
                tooltipIcon: 'Info',
                ...statusBaseConfig.actions.EditService,
                ...statusBaseConfig.menu.EditOnly,
            },
            'Provisioning SBC': {
                color: 'green',
                animation: true,
                tooltip:
                    'SBC is being provisioned for Direct Routing.  Please wait',
                tooltipIcon: 'Spinner',
                ...statusBaseConfig.actions.None,
                showEdit: false,
                grantAccess: false,
                showTenantId: false,
            },
            statusLabels: [
                'No Voice Policies',
                'No Dial Plans',
                'Provisioning SBC',
            ],
        },
        12: {
            'No Voice Policies': {
                color: 'yellow',
                animation: false,
                tooltip:
                    'Direct Routing requires at least one Voice Policy to be assigned to the service.  Please edit service to continue',
                tooltipIcon: 'Info',
                ...statusBaseConfig.actions.EditService,
                ...statusBaseConfig.menu.EditOnly,
            },
            'No Dial Plans': {
                color: 'yellow',
                animation: false,
                tooltip:
                    'Direct Routing requires at least one Dial Plan to be assigned to the service.  Please edit service to continue',
                tooltipIcon: 'Info',
                ...statusBaseConfig.actions.EditService,
                ...statusBaseConfig.menu.EditOnly,
            },
            'Activating Teams Phone': {
                color: 'green',
                animation: true,
                tooltip:
                    'Enabling Enterprise Voice on new domains. Please wait',
                tooltipIcon: 'Spinner',
                ...statusBaseConfig.actions.None,
                showEdit: true,
                grantAccess: false,
                showTenantId: false,
            },
            statusLabels: [
                'No Voice Policies',
                'No Dial Plans',
                'Activating Teams Phone',
            ],
        },
        13: {
            'No Voice Policies': {
                color: 'yellow',
                animation: false,
                tooltip:
                    'Direct Routing requires at least one Voice Routing Policy to be assigned to the service.  Please edit service to continue',
                tooltipIcon: 'Info',
                ...statusBaseConfig.actions.EditService,
                ...statusBaseConfig.menu.EditOnly,
            },
            'No Dial Plans': {
                color: 'yellow',
                animation: false,
                tooltip:
                    'Direct Routing requires at least one Dial Plan to be assigned to the service.  Please edit service to continue',
                tooltipIcon: 'Info',
                ...statusBaseConfig.actions.EditService,
                ...statusBaseConfig.menu.EditOnly,
            },
            'Provisioning Teams Phone': {
                color: 'green',
                animation: true,
                tooltip: 'Creating Voice Routing Policies and Dial Plans',
                tooltipIcon: 'Spinner',
                ...statusBaseConfig.actions.None,
                showEdit: true,
                grantAccess: false,
                showTenantId: false,
            },
            statusLabels: [
                'No Voice Policies',
                'No Dial Plans',
                'Provisioning Teams Phone',
            ],
        },
        14: {
            'Pending Activation': {
                color: 'yellow',
                animation: true,
                tooltip:
                    'Configuration requires changes within Teams to fully activate calling services.',
                tooltipIcon: 'ExternalLink',
                ...statusBaseConfig.actions.SyncSetupGuide,
                ...statusBaseConfig.actions.ConfirmActivation,
                link: '/Guides/GCCH_Activation.pdf',
                showEdit: true,
                grantAccess: false,
                showTenantId: false,
                showActivationMenu: true,
            },
            statusLabels: ['Pending Activation'],
        },
        20: {
            Error: S_COMMON_ERROR,
            statusLabels: ['Error'],
        },
    },
    statusIds: [0, 2, 3, 4, 10, 11, 12, 13, 14, 20],
};

export const zoomAuthData = {
    statusId: {
        0: {
            Healthy: S_HEALTHY,
            statusLabels: ['Healthy'],
        },
        4: {
            'Pair Pending': S_CONFIRMING_ACCESS,
            statusLabels: ['Pair Pending'],
        },
        10: {
            'Pair Pending': S_CONFIRMING_ACCESS,
            statusLabels: ['Pair Pending'],
        },
        20: {
            Error: S_COMMON_ERROR,
            statusLabels: ['Error'],
        },
        21: {
            Degraded: S_DEGRADED_NO_CONNECT,
            statusLabels: ['Degraded'],
        },
    },
    statusIds: [0, 4, 10, 20, 21],
};

export const zoomData = {
    statusId: {
        0: {
            Healthy: S_HEALTHY,
            statusLabels: ['Healthy'],
        },
        2: {
            'Access Required': {
                color: 'yellow',
                animation: false,
                tooltip: ZOOM_NO_CONFIG,
                tooltipIcon: 'Info',
                ...statusBaseConfig.actions.GrantAccess,
                ...statusBaseConfig.menu.EditAndAccess,
            },
            statusLabels: ['Access Required'],
        },
    },
    statusIds: [0, 2],
};

// Identical structure to zoomAuthData
export const webexAuthData = zoomAuthData;
export const ringCentralAuthData = zoomAuthData;
export const dialPadAuthData = zoomAuthData;

export const webexData = {
    statusId: {
        0: {
            Healthy: S_HEALTHY,
            statusLabels: ['Healthy'],
        },
        2: {
            'Access Required': {
                color: 'yellow',
                animation: false,
                tooltip: WEBEX_NO_CONFIG,
                tooltipIcon: 'Info',
                ...statusBaseConfig.actions.GrantAccess,
                ...statusBaseConfig.menu.EditAndAccess,
            },
            statusLabels: ['Access Required'],
        },
        20: {
            Error: S_COMMON_ERROR,
            statusLabels: ['Error'],
        },
        21: {
            Degraded: S_DEGRADED_NO_CONNECT,
            statusLabels: ['Degraded'],
        },
        // Update with real status when it is ready
        10: {
            'Provisioning Trunk': {
                color: 'green',
                animation: true,
                tooltip: null,
                ...statusBaseConfig.menu.EditAndAccess,
                ...statusBaseConfig.actions.GrantAccess,
            },
            statusLabels: ['Provisioning Trunk'],
        },
    },
    statusIds: [0, 10, 2, 20, 21],
};

export const eightByEightAuthData = {
    statusId: {
        0: {
            Healthy: S_HEALTHY,
            statusLabels: ['Healthy'],
        },
        4: {
            'Pair Pending': S_CONFIRMING_ACCESS,
            statusLabels: ['Pair Pending'],
        },
        10: {
            'Pair Pending': S_CONFIRMING_ACCESS,
            statusLabels: ['Pair Pending'],
        },
        20: {
            Error: S_COMMON_ERROR,
            statusLabels: ['Error'],
        },
        21: {
            'Incomplete Config': S_INCOMPLETE_CONFIG,
            statusLabels: ['Incomplete Config'],
        },
    },
    statusIds: [0, 4, 10, 20, 21],
};

export const ringCentralData = {
    statusId: {
        0: {
            Healthy: S_HEALTHY,
            statusLabels: ['Healthy'],
        },
        1: {
            'Pending Access': {
                color: 'yellow',
                animation: false,
                tooltip: RINGCENTRAL_NO_CONFIG,
                tooltipIcon: 'Info',
                ...statusBaseConfig.actions.GrantAccess,
                ...statusBaseConfig.menu.EditAndAccess,
                grantAccess: true,
            },
            statusLabels: ['Pending Access'],
        },
        2: {
            'Access Required': {
                color: 'yellow',
                animation: false,
                tooltip: RINGCENTRAL_NO_CONFIG,
                tooltipIcon: 'Info',
                ...statusBaseConfig.actions.GrantAccess,
                ...statusBaseConfig.menu.EditAndAccess,
                showEdit: true,
                grantAccess: true,
                showTenantId: true,
            },
            statusLabels: ['Access Required'],
        },
        20: {
            Error: {
                color: 'red',
                animation: false,
                tooltip: ERROR_TOOLTIP,
                tooltipIcon: 'Warning',
                ...statusBaseConfig.actions.GrantAccess,
                ...statusBaseConfig.menu.EditAndAccess,
                grantAccess: true,
            },
            statusLabels: ['Error'],
        },
    },
    statusIds: [0, 1, 2, 20],
};

// Identical structure to ringCentralData
export const dialPadData = {
    statusId: {
        0: {
            Healthy: S_HEALTHY,
            statusLabels: ['Healthy'],
        },
        1: {
            'Pending Access': {
                color: 'yellow',
                animation: false,
                tooltip: DIALPAD_NO_CONFIG,
                tooltipIcon: 'Info',
                ...statusBaseConfig.actions.GrantAccess,
                ...statusBaseConfig.menu.EditAndAccess,
                grantAccess: true,
            },
            statusLabels: ['Pending Access'],
        },
        2: {
            'Access Required': {
                color: 'yellow',
                animation: false,
                tooltip: DIALPAD_NO_CONFIG,
                tooltipIcon: 'Info',
                ...statusBaseConfig.actions.GrantAccess,
                ...statusBaseConfig.menu.EditAndAccess,
                showEdit: true,
                grantAccess: true,
                showTenantId: true,
            },
            statusLabels: ['Access Required'],
        },
        20: {
            Error: {
                color: 'red',
                animation: false,
                tooltip: ERROR_TOOLTIP,
                tooltipIcon: 'Warning',
                ...statusBaseConfig.actions.GrantAccess,
                ...statusBaseConfig.menu.EditAndAccess,
                grantAccess: true,
            },
            statusLabels: ['Error'],
        },
    },
    statusIds: [0, 1, 2, 20],
};

// 8x8 is a Service Card (not an App Auth card) with no Grant Access flow, so
// every status uses EditOnly + actions.None. Backend EightByEight status enum:
// 0=Ok, 1=Creating, 2=PendingAccess, 10=UpdatingAddresses, 11=UpdatingSites,
// 12=CheckingLicensing, 20=Error.
export const eightByEightData = {
    statusId: {
        0: {
            // Ok — provisioning complete
            Healthy: S_HEALTHY,
            statusLabels: ['Healthy'],
        },
        1: {
            // Creating — transitional
            Creating: {
                color: 'green',
                animation: true,
                tooltip: 'Creating service… please wait',
                tooltipIcon: 'Spinner',
                ...statusBaseConfig.actions.None,
                ...statusBaseConfig.menu.EditOnly,
            },
            statusLabels: ['Creating'],
        },
        2: {
            // PendingAccess
            'Pending Access': {
                color: 'yellow',
                animation: false,
                tooltip: 'Pending access. Please continue the setup process.',
                tooltipIcon: 'Info',
                ...statusBaseConfig.actions.None,
                ...statusBaseConfig.menu.EditOnly,
            },
            statusLabels: ['Pending Access'],
        },
        10: {
            // UpdatingAddresses — transitional
            'Updating Addresses': {
                color: 'green',
                animation: true,
                tooltip: 'Updating addresses… please wait',
                tooltipIcon: 'Spinner',
                ...statusBaseConfig.actions.None,
                ...statusBaseConfig.menu.EditOnly,
            },
            statusLabels: ['Updating Addresses'],
        },
        11: {
            // UpdatingSites — transitional
            'Updating Sites': {
                color: 'green',
                animation: true,
                tooltip: 'Updating sites… please wait',
                tooltipIcon: 'Spinner',
                ...statusBaseConfig.actions.None,
                ...statusBaseConfig.menu.EditOnly,
            },
            statusLabels: ['Updating Sites'],
        },
        12: {
            // CheckingLicensing — transitional
            'Checking Licensing': {
                color: 'green',
                animation: true,
                tooltip: 'Checking licensing… please wait',
                tooltipIcon: 'Spinner',
                ...statusBaseConfig.actions.None,
                ...statusBaseConfig.menu.EditOnly,
            },
            statusLabels: ['Checking Licensing'],
        },
        20: {
            // Error
            Error: {
                color: 'red',
                animation: false,
                tooltip:
                    'Failed to set up the 8x8 service. Please contact your service provider.',
                tooltipIcon: 'Warning',
                ...statusBaseConfig.actions.None,
                ...statusBaseConfig.menu.EditOnly,
            },
            statusLabels: ['Error'],
        },
    },
    statusIds: [0, 1, 2, 10, 11, 12, 20],
};

// Maps the 8x8 list-level statusDisplay.statusDisplayDetail to the numeric
// status in eightByEightData; used as a fallback when per-service status is absent.
export const EIGHTBYEIGHT_STATUS_DISPLAY_DETAIL_TO_STATUS = {
    Ok: 0,
    Creating: 1,
    PendingAccess: 2,
    UpdatingAddresses: 10,
    UpdatingSites: 11,
    CheckingLicensing: 12,
    Error: 20,
};

export const acsData = {
    statusId: {
        0: {
            color: 'green',
            isAnimated: false,
            hasStripes: false,
            status: 'Healthy',
        },
        1: {
            color: 'gray',
            isAnimated: false,
            hasStripes: false,
            status: '...',
            to: null,
            toText: null,
        },
    },
};

// ─── Service Registry ─────────────────────────────────────────────────────────

export const initServiceData = {
    services: {
        [MICROSOFT_GRAPH]: appAuthData,
        [APP_AUTH]: appAuthData,
        [APP_AUTH_GCCH]: gccHighAppAuthData,
        [OPERATOR_CONNECT]: operatorConnectData,
        [DIRECT_ROUTING]: directRoutingData,
        [ZOOM_AUTH]: zoomAuthData,
        [ZOOM]: zoomData,
        [EIGHTBYEIGHT_AUTH]: eightByEightAuthData,
        [EIGHTBYEIGHT]: eightByEightData,
        [ACS]: acsData,
        [GCC_HIGH]: gccHighData,
        [WEBEX]: webexData,
        [WEBEX_AUTH]: webexAuthData,
        [RINGCENTRAL_AUTH]: ringCentralAuthData,
        [RINGCENTRAL]: ringCentralData,
        [DIALPAD_AUTH]: dialPadAuthData,
        [DIALPAD]: dialPadData,
    },
    serviceTypes: [
        MICROSOFT_GRAPH,
        APP_AUTH,
        APP_AUTH_GCCH,
        ZOOM_AUTH,
        OPERATOR_CONNECT,
        DIRECT_ROUTING,
        ZOOM,
        EIGHTBYEIGHT_AUTH,
        EIGHTBYEIGHT,
        ACS,
        GCC_HIGH,
        WEBEX,
        WEBEX_AUTH,
        RINGCENTRAL_AUTH,
        RINGCENTRAL,
        DIALPAD_AUTH,
        DIALPAD,
    ],
};

export const serviceData = {
    services: {
        AppAuth: appAuthData,
        OperatorConnect: operatorConnectData,
        DirectRouting: directRoutingData,
        ZoomPEX: zoomAuthData,
        Zoom: zoomData,
        EightByEightAppAuth: eightByEightAuthData,
        EightByEight: eightByEightData,
        ACSDirectRouting: acsData,
        GCCHigh: gccHighData,
        GCCHighAppAuth: gccHighAppAuthData,
        Webex: webexData,
        WebexAppAuth: webexAuthData,
    },
    serviceTypes: [
        APP_AUTH,
        ZOOM_AUTH,
        OPERATOR_CONNECT,
        DIRECT_ROUTING,
        ZOOM,
        EIGHTBYEIGHT_AUTH,
        EIGHTBYEIGHT,
        ACS,
        GCC_HIGH,
        WEBEX,
        WEBEX_AUTH,
        SBC,
        SRT,
    ],
};

// ─── Utility Functions ────────────────────────────────────────────────────────

export const isSyncEnabledServiceType = (serviceType) => {
    const syncEnabled = {
        MICROSOFT_GRAPH,
        APP_AUTH,
        OPERATOR_CONNECT,
        DIRECT_ROUTING,
        // (3107): always on for GCC High DR, shown for consistency with DR.
        GCC_HIGH,
        ZOOM_AUTH,
        WEBEX_AUTH,
        EIGHTBYEIGHT,
    };
    return Object.values(syncEnabled).includes(serviceType);
};

export const canEditService = (serviceType) => {
    const editableServices = {
        ACS,
        OPERATOR_CONNECT,
        ZOOM_AUTH,
        WEBEX_AUTH,
        DIRECT_ROUTING,
        MICROSOFT_GRAPH,
        GCC_HIGH,
    };
    return Object.values(editableServices).includes(serviceType);
};

export const doesUseServiceDashboard = (serviceType) => {
    const serviceDashboardEnabled = {
        APP_AUTH,
        OPERATOR_CONNECT,
        DIRECT_ROUTING,
        ZOOM,
        EIGHTBYEIGHT,
        SBC,
        WEBEX,
        SRT,
    };
    return Object.values(serviceDashboardEnabled).includes(serviceType);
};

export const doesSetSelectedServiceProfile = (serviceType) => {
    const setSelectedServiceProfileEnabled = {
        OPERATOR_CONNECT,
        ZOOM,
        EIGHTBYEIGHT,
        WEBEX,
    };
    return Object.values(setSelectedServiceProfileEnabled).includes(
        serviceType,
    );
};

// ─── UI / Query Constants ─────────────────────────────────────────────────────

export const STATUS_CHANGE = 'STATUS_CHANGE';
export const changed_status = 'changed_status';
export const loaded_status = 'loaded_status';

export const QUERY_CACHE_TIME = 1; // cache time set to 15 secs. (converted to milliseconds)
export const REFETCH_INTERVAL = 15 * 1000; // refetch interval set to 15 secs. (converted to milliseconds)

export const UCAAS_ACCORDION = 'Platform Services';
export const VENDOR_DEFINED_ACCORDION = 'Value Add Services';
export const OPERATOR_PLATFORM_ACCORDION = 'Operator Platform Services';

// (2941) Shown as a tooltip beside each Add Service section header.
export const ADD_SERVICE_SECTION_DESCRIPTIONS = {
    [UCAAS_ACCORDION]:
        'Platform Services provide full automation and orchestration facilities for leading UCaaS service providers. Through Platform Services, customers may manage configuration of calling, users, applications and more, via a single pane of glass.',
    [OPERATOR_PLATFORM_ACCORDION]:
        "These services enable full TCAP management of all associated and managed services within the service provider's portfolio. These are created by and offered to customers by the service provider.",
    [VENDOR_DEFINED_ACCORDION]:
        "Value add services are provided by third parties to provide greater extensibility and power to a customer's UCaaS strategy. Within here, you may find services such as Call Recording, Business Analytics, Contact Center and more, offered by the service provider with fully automated provisioning.",
};

export const statuses = (displayedName) => ({
    green: { type: 'active', displayedName: displayedName || 'Healthy' },
    red: { type: 'inactive', displayedName: displayedName },
    yellow: { type: 'processing', displayedName: displayedName || 'Unknown' },
    gray: {
        type: 'disabled',
        displayedName: displayedName || 'Unknown',
    },
});

export const statusPriorityOrder = [-1, 12, 2, 11, 1, 10, 0];

export const drStatusesColour = {
    '-1': { color: 'gray' },
    0: { color: 'green', animated: false },
    10: { color: 'yellow', animated: true },
    1: { color: 'yellow', animated: false },
    11: { color: 'yellow', animated: true },
    2: { color: 'red', animated: false },
    12: { color: 'red', animated: true },
};
